/* SIZESOFTX
=========================================================================================
* Descripción: Context del catálogo de Almacenes Exhibidores
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 17/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { isErrorForm, handleValidarStrings, getDatePost } from '../../settings/utils';
import { defaultTypes } from "../../settings/enums";
import {
    apiAlmacenesExhibidoresList,
    apiAlmacenesExhibidoresSave,
    apiAlmacenesExhibidoresDelete,
} from "../../settings/ApiConfig";
import { useRequest } from "../System/RequestContext";
import { useLayout } from "../System/LayoutContext";

const getStructAlmacenesExhibidores = () => ({
    AlmacenExhibidor: "",
    Status: defaultTypes.String,
});

const getStructAlmacenesExhibidoresPayload = () => ({
    AlmacenExhibidor: { value: "", error: false, helper: "" },
    Status: { value: defaultTypes.String, error: false, helper: "" },
});

export const AlmacenesExhibidoresContext = createContext();
export const AlmacenesExhibidoresProvider = (props) => {
    const { GetRequest, PostRequest, DeleteRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const navigation = useNavigate();

    const [almacenesExhibidoresSeleccionado, setAlmacenesExhibidoresSeleccionado] = useState(getStructAlmacenesExhibidores());
    const [almacenesExhibidoresPayLoad, setAlmacenesExhibidoresPayLoad] = useState(getStructAlmacenesExhibidoresPayload());
    const [listaAlmacenesExhibidores, setListaAlmacenesExhibidores] = useState([]);
    const [statusEditAlmacenesExhibidores, setStatusEditAlmacenesExhibidores] = useState(true); // invertido para disabled
    const [statusEnableFields, setStatusEnableFields] = useState(true); // true = campos deshabilitados
    const [statusOpenModalEliminarAlmacenesExhibidores, setStatusOpenModalEliminarAlmacenesExhibidores] = useState(false);
    const [statusOpenModalBuscarAlmacenesExhibidores, setStatusOpenModalBuscarAlmacenesExhibidores] = useState(false);

    const handleClickRegresarMainDashboard = () => {
        navigation("/*");
    };

    const handleOpenModalBuscarAlmacenesExhibidores = () => {
        setStatusOpenModalBuscarAlmacenesExhibidores(true);
    };

    const handleCloseModalBuscarAlmacenesExhibidores = () => {
        setStatusOpenModalBuscarAlmacenesExhibidores(false);
    };

    const handleAlmacenesExhibidoresSeleccionadoFromModal = (almacenExhibidor) => {
        if (almacenExhibidor && almacenExhibidor.AlmacenExhibidor) {
            setAlmacenesExhibidoresSeleccionado(almacenExhibidor);
            const payload = getStructAlmacenesExhibidoresPayload();
            Object.keys(payload).forEach((key) => {
                if (almacenExhibidor[key] !== undefined) {
                    payload[key] = { value: almacenExhibidor[key] ?? "", error: false, helper: "" };
                }
            });
            setAlmacenesExhibidoresPayLoad(payload);
            setStatusEditAlmacenesExhibidores(true); // Modo solo lectura
            setStatusEnableFields(true); // Campos deshabilitados
        }
    };

    // ─── Handlers de datos ────────────────────────────────────────────

    const handleChangeDataAlmacenesExhibidores = (event, newValue, fieldName) => {
        if (event?.target?.name) {
            const { name, value } = event.target;
            setAlmacenesExhibidoresSeleccionado(prev => ({ ...prev, [name]: value }));
            return;
        }
        if (newValue !== null && fieldName) {
            const value = typeof newValue === "string" ? newValue : newValue?.[fieldName] || "";
            setAlmacenesExhibidoresSeleccionado(prev => ({ ...prev, [fieldName]: value }));
        }
    };

    const handleChangeDataAlmacenesExhibidoresPayload = (e, valor, field) => {
        let name = field ? field : e.target.name ?? "";
        let value = valor !== undefined ? valor : e.target.value ?? "";
        let error = false;
        let helper = "";

        const mensajeError = handleValidarStrings(value, name);
        if (mensajeError) {
            error = true;
            helper = mensajeError;
        }

        const lower = String(value).toLowerCase();
        if (!error && (
            lower.includes("<script") ||
            lower.includes("</script") ||
            lower.includes("javascript:") ||
            lower.includes("onerror=") ||
            lower.includes("onload=")
        )) {
            error = true;
            helper = "Intento de inyección detectado y bloqueado en: (" + name + ")";
        }

        setAlmacenesExhibidoresPayLoad(prev => {
            const prevValue = prev[name]?.value ?? "";
            const newValueStr = String(value ?? "");
            const prevValueStr = String(prevValue ?? "");
            if (prevValueStr === newValueStr) return prev;
            return {
                ...prev,
                [name]: { ...prev[name], value, error, helper },
            };
        });
    };

    // ─── CRUD ─────────────────────────────────────────────────────────

    const handleObtenerAlmacenesExhibidoresList = async () => {
        const result = await GetRequest({
            url: apiAlmacenesExhibidoresList,
            loader: "Cargando lista de almacenes exhibidores...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al obtener la lista de almacenes exhibidores: " + result.message);
            return;
        }
      
        setListaAlmacenesExhibidores(result?.result ?? []);
    };

    const handleClickNuevoAlmacenesExhibidores = () => {
        handleClickDeshacer();
        setStatusEditAlmacenesExhibidores(false);
        setStatusEnableFields(false); // Habilitar campos para nuevo almacén exhibidor
    };

    const handleClickEditarAlmacenesExhibidores = () => {
        setStatusEditAlmacenesExhibidores(false);
        setStatusEnableFields(false); // Habilitar campos para edición
    };

    const handleClickDeshacer = () => {
        setAlmacenesExhibidoresSeleccionado(getStructAlmacenesExhibidores());
        setAlmacenesExhibidoresPayLoad(getStructAlmacenesExhibidoresPayload());
        setStatusEditAlmacenesExhibidores(true);
        setStatusEnableFields(true); // Deshabilitar campos
    };

    const handleClickGuardarAlmacenesExhibidores = async () => {
        if (
            almacenesExhibidoresPayLoad.AlmacenExhibidor.value.trim().length === 0
        ) {
            handleOpenAlert("Es necesario especificar al menos el nombre del almacén exhibidor para continuar!");
            return;
        }

        const nombreInput = almacenesExhibidoresPayLoad.AlmacenExhibidor.value.trim().toUpperCase();
        const esModoNuevo = !almacenesExhibidoresSeleccionado?.AlmacenExhibidor || almacenesExhibidoresSeleccionado.AlmacenExhibidor.trim() === "";

        // Validar que no se repita en la lista
        const existe = listaAlmacenesExhibidores.some(
            (almacen) => almacen.AlmacenExhibidor && almacen.AlmacenExhibidor.trim().toUpperCase() === nombreInput
        );

        if (esModoNuevo && existe) {
            handleOpenAlert("El nombre del almacén exhibidor ya existe en la lista. Por favor, especifique uno diferente.", "error");
            return;
        }

        if (isErrorForm(almacenesExhibidoresPayLoad)) {
            handleOpenAlert("Existen errores en los datos del almacén exhibidor y no es posible continuar!");
            return;
        }

        const resultPayload = {};
        const upperIfString = (v, key) => {
            if (typeof v !== "string") return v;
            // No hay campos que no deban ir en mayúsculas para almacenes exhibidores
            return v.toUpperCase();
        };

        Object.entries(almacenesExhibidoresPayLoad).forEach(([key, meta]) => {
            if (meta && typeof meta === "object" && "value" in meta) {
                resultPayload[key] = upperIfString(meta.value, key);
            } else {
                resultPayload[key] = upperIfString(meta, key);
            }
        });

        const result = await PostRequest({
            url: apiAlmacenesExhibidoresSave,
            body: resultPayload,
            loader: "Guardando datos del almacén exhibidor...",
        });

        if (result.hasError || result.errors) {
            handleOpenAlert("Error al guardar datos del almacén exhibidor: " + result.message);
            return;
        }

        handleOpenAlert("Almacén exhibidor guardado correctamente", "ok");
        setStatusEditAlmacenesExhibidores(true);
        setStatusEnableFields(true); // Deshabilitar campos
        
        // Si el backend devuelve el almacén exhibidor con clave generada, usarla
        if (result.result && result.result.AlmacenExhibidor) {
            setAlmacenesExhibidoresSeleccionado(result.result);
            const payload = getStructAlmacenesExhibidoresPayload();
            Object.keys(payload).forEach((key) => {
                if (result.result[key] !== undefined) {
                    payload[key] = { value: result.result[key] ?? "", error: false, helper: "" };
                }
            });
            setAlmacenesExhibidoresPayLoad(payload);
        } else {
            setAlmacenesExhibidoresSeleccionado(resultPayload);
        }
        
        await handleObtenerAlmacenesExhibidoresList();
    };

     const handleClickEliminarAlmacenesExhibidores = async () => {
         if (!almacenesExhibidoresSeleccionado.AlmacenExhibidor || almacenesExhibidoresSeleccionado.AlmacenExhibidor.trim().length === 0) return;
 
         const result = await DeleteRequest({
             url: apiAlmacenesExhibidoresDelete.replace("{nombre}", encodeURIComponent(almacenesExhibidoresSeleccionado.AlmacenExhibidor)),
             body: {},
             loader: "Eliminando almacén exhibidor...",
         });
 
         if (result.hasError || result.errors) {
             handleOpenAlert("Error al eliminar el almacén exhibidor: " + result.message);
             return;
         }
 
         handleOpenAlert("Almacén exhibidor eliminado correctamente", "ok");
         setStatusOpenModalEliminarAlmacenesExhibidores(false);
         await handleObtenerAlmacenesExhibidoresList();
         handleClickDeshacer(); // Limpiar formulario después de eliminar
     };

    const handleClickRefrescarDatos = async () => {
        await handleObtenerAlmacenesExhibidoresList();
        handleOpenAlert("Datos actualizados correctamente", "ok");
    };

    // ─── Modales ──────────────────────────────────────────────────────

    return (
        <AlmacenesExhibidoresContext.Provider
            value={{
                almacenesExhibidoresSeleccionado, setAlmacenesExhibidoresSeleccionado,
                almacenesExhibidoresPayLoad, setAlmacenesExhibidoresPayLoad, getStructAlmacenesExhibidoresPayload,
                listaAlmacenesExhibidores,
                statusEditAlmacenesExhibidores, setStatusEditAlmacenesExhibidores,
                statusEnableFields,
                statusOpenModalEliminarAlmacenesExhibidores, setStatusOpenModalEliminarAlmacenesExhibidores,
                statusOpenModalBuscarAlmacenesExhibidores, setStatusOpenModalBuscarAlmacenesExhibidores,
                handleClickRegresarMainDashboard,
                handleOpenModalBuscarAlmacenesExhibidores,
                handleCloseModalBuscarAlmacenesExhibidores,
                handleAlmacenesExhibidoresSeleccionadoFromModal,
                handleChangeDataAlmacenesExhibidores,
                handleChangeDataAlmacenesExhibidoresPayload,
                handleObtenerAlmacenesExhibidoresList,
                handleClickNuevoAlmacenesExhibidores,
                handleClickEditarAlmacenesExhibidores,
                handleClickDeshacer,
                handleClickGuardarAlmacenesExhibidores,
                handleClickEliminarAlmacenesExhibidores,
                handleClickRefrescarDatos,
            }}
        >
            {props.children}
        </AlmacenesExhibidoresContext.Provider>
    );
};

export const useAlmacenesExhibidores = () => useContext(AlmacenesExhibidoresContext);